import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { LookupItem } from '../../store/api/saleContractApi'
import {
  type SalePTBFFixation,
  type SalePTBFFixationPayload,
  type SalePTBFFixationRow,
  type SalePTBFFixationRowUpdate,
  useCreateSalePTBFFixationMutation,
  useGetSalePTBFFixationLotToFixQuery,
  useGetSalePTBFFixationQuery,
  useGetSalePTBFFixationRowsQuery,
  useLazyGetSalePTBFFixationContractsQuery,
  useUpdateSalePTBFFixationEditOptionsMutation,
  useUpdateSalePTBFFixationRowMutation,
  useCancelSalePTBFFixationMutation,
} from '../../store/api/saleContractApi'
import ConfirmDialog from '../../components/ConfirmDialog'
import { useSnackbar } from '../../hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../../theme/responsive'
import { getErrorMessage } from '../../utils/error'

interface SalePTBFFixationFormDialogProps {
  open: boolean
  /**
   * Initial contract no (khi user chọn row → pre-select trong autocomplete).
   * `null/undefined` = mở form trắng, user tự chọn contract từ Autocomplete.
   * Note: field `mode` đã bỏ — form auto-detect:
   *   subformRows.length === 0 → Create flow (POST createFixation với first row)
   *   subformRows.length >  0 → Edit flow (PUT updateEditOptions header only)
   */
  contractNo?: string | null
  onClose: () => void
}

const fmtNumStr = (s: string): string => {
  if (!s) return ''
  const neg = s.startsWith('-')
  const abs = neg ? s.slice(1) : s
  const [intPart, ...rest] = abs.split('.')
  const intFormatted = intPart ? Number(intPart).toLocaleString('en-US') : '0'
  const result = rest.length > 0 ? `${intFormatted}.${rest.join('.')}` : intFormatted
  return neg ? `-${result}` : result
}

const numSx = { '& input': { textAlign: 'right' as const } } as const

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatNum = (value: number | null | undefined): string => {
  if (value == null) return ''
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface SalePTBFFixationFormModel {
  contractNo: string
  dateFix: string
  releaseDate: string
  lotsFixed: string
  levelFixed: string
  isUpload: boolean
  isCusSign: boolean
}

const today = () => new Date().toISOString().slice(0, 10)

const emptyForm = (): SalePTBFFixationFormModel => ({
  contractNo: '',
  dateFix: today(),
  releaseDate: today(),
  lotsFixed: '' as string,
  levelFixed: '' as string,
  isUpload: false,
  isCusSign: false,
})

const safeDate = (value: unknown): string => {
  if (!value) return ''
  const text = String(value)
  return text.length >= 10 ? text.slice(0, 10) : text
}

const toNum = (value: string): number | null => {
  if (!value) return null
  const n = Number(value)
  return isNaN(n) ? null : n
}

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object' || !('data' in error)) return 'Failed to save PTBF Fixation. Please try again.'
  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save PTBF Fixation. Please try again.'
  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string') return msg
    }
    return JSON.stringify(first)
  }
  return 'Failed to save PTBF Fixation. Please try again.'
}

// Skill `/convert-form` readonly standard: neutral gray thay vì `action.hover`
// (overlay phớt hồng trên theme TLC cream).
const roSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
    cursor: 'default',
  },
}

const dialogContentSx = {
  pt: 0.75,
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
  '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiCheckbox-root': { p: 0.5 },
  '& .MuiSvgIcon-root': { fontSize: '1rem' },
  '& .MuiTypography-body2': { fontSize: '0.8125rem' },
  '& .MuiSelect-select': { fontSize: '0.8125rem' },
}


/* ──────────────────────────────────────────────
   Main form dialog
────────────────────────────────────────────── */
export default function SalePTBFFixationFormDialog({ open, contractNo, onClose }: SalePTBFFixationFormDialogProps) {
  const { canEdit } = usePagePermission()
  const [form, setForm] = useState<SalePTBFFixationFormModel>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  // editingRowId !== null → subform input đang edit row đó (PATCH on save).
  // editingRowId === null → input area dành cho row mới (POST on save).
  const [editingRowId, setEditingRowId] = useState<number | null>(null)
  // Autocomplete Contract No: debounce input để tránh spam BE search mỗi keystroke.
  const [contractSearchText, setContractSearchText] = useState('')

  const [fetchContracts, { currentData: contractOptions, isFetching: contractsLoading }] =
    useLazyGetSalePTBFFixationContractsQuery()
  // `form.contractNo` = Autocomplete value → drive 3 query bên dưới. Khi user
  // đổi contract trong Autocomplete → arg change → query tự refetch (default).
  // Mutation (create/update/cancel) invalidate tag `'SalePTBFFixation'` →
  // 3 query đều tự refetch (lotToFix vừa được thêm providesTags trong API slice).
  // KHÔNG dùng `refetchOnMountOrArgChange: true` để tránh refetch thừa khi
  // dialog remount nhưng cache còn fresh — giảm 3 round-trip mỗi lần mở form.
  const activeContractNo = form.contractNo
  const { data: editData, isFetching: editLoading } = useGetSalePTBFFixationQuery(activeContractNo, {
    skip: !open || !activeContractNo,
  })
  const { data: lotToFixData } = useGetSalePTBFFixationLotToFixQuery(activeContractNo, {
    skip: !open || !activeContractNo,
  })
  const { data: rowsData, isFetching: rowsFetching } = useGetSalePTBFFixationRowsQuery(activeContractNo, {
    skip: !open || !activeContractNo,
  })
  const snack = useSnackbar()
  const [rowDeleteTarget, setRowDeleteTarget] = useState<SalePTBFFixationRow | null>(null)
  const [createFixation, { isLoading: creating }] = useCreateSalePTBFFixationMutation()
  const [updateEditOptions, { isLoading: updating }] = useUpdateSalePTBFFixationEditOptionsMutation()
  const [cancelFixation, { isLoading: isDeleting }] = useCancelSalePTBFFixationMutation()
  const [updateRow, { isLoading: updatingRow }] = useUpdateSalePTBFFixationRowMutation()

  const contractItems = useMemo(() => contractOptions?.items ?? [], [contractOptions])
  const lotToFixRaw = lotToFixData?.lotToFix ?? lotToFixData?.balanceLotsTbf ?? null
  const subformRows = useMemo(() => rowsData?.items ?? [], [rowsData])
  const rowCount = subformRows.length

  // Khi user click Edit row → BE đã trừ `lotsFixed` của row đó khỏi lotToFix.
  // Để validation & UI cho phép user nhập lại số cũ (hoặc đổi nhỏ/lớn hơn),
  // cộng lại `lotsFixed` original của row đang edit vào lotToFix hiển thị.
  // Ví dụ: total=10, row A lotsFixed=6 → BE lotToFix=4. Khi Edit row A,
  // hiển thị Lot to Fix = 4 + 6 = 10 (room cho row A re-fix toàn bộ).
  const editingRowOriginal = useMemo(
    () => (editingRowId != null ? subformRows.find((r) => r.ptbfFixationId === editingRowId) : null),
    [editingRowId, subformRows],
  )
  const editingRowOriginalLots = Number(editingRowOriginal?.lotsFixed ?? 0)
  const lotToFix = lotToFixRaw != null ? lotToFixRaw + editingRowOriginalLots : null
  // Form luôn unified — subform input area + subform table cùng hiển thị.
  // Khi rowCount > 0 → đã có header data (load qua editData effect bên dưới).
  // editingRowId track row đang edit trong subform input.
  const hasExistingFixation = rowCount > 0

  // Reset form khi dialog mở. Nếu list view truyền sẵn contractNo (user đã chọn
  // row) → pre-fill autocomplete để load data ngay. Else mở trắng.
  useEffect(() => {
    if (!open) return
    setValidationError(null)
    setFieldErrors({})
    setEditingRowId(null)
    setForm({ ...emptyForm(), contractNo: contractNo ?? '' })
    // Pre-fill input text bằng contractNo (sẽ được swap qua label thật khi
    // contractItems load xong qua effect khác).
    setContractSearchText(contractNo ?? '')
    fetchContracts(contractNo ?? undefined, false)
  }, [open, contractNo, fetchContracts])

  // Khi contractItems load xong và form.contractNo đã set → sync inputValue
  // với label thật của option (vd "C-001 - Client ABC") thay vì raw value.
  useEffect(() => {
    if (!form.contractNo) return
    const found = contractItems.find((o) => o.value === form.contractNo)
    if (found && contractSearchText !== found.label) {
      setContractSearchText(found.label)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractItems, form.contractNo])

  // Debounce 300ms cho contract search → tránh spam BE mỗi keystroke. Bỏ qua
  // initial mount (đã fetch trong effect trên với prefix=contractNo).
  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => {
      fetchContracts(contractSearchText || undefined, false)
    }, 300)
    return () => window.clearTimeout(t)
  }, [open, contractSearchText, fetchContracts])

  // Khi load editData (header fixation của contract đã chọn) → fill form header.
  // Chỉ fill khi có rows existing. Subform input area giữ trống cho user nhập
  // row mới hoặc click Edit trong subform table để load row đó.
  //
  // Hydrate CHỈ 1 LẦN cho mỗi contract (ref track contract đã hydrate). Lý do:
  // saveRow() invalidate tag → editData refetch → nếu hydrate lại mỗi lần
  // refetch sẽ ĐÈ giá trị header user vừa sửa (vd untick Customer Signed bị
  // reset về tick từ server). Mở dialog / đổi contract mới hydrate lại.
  // (memory feedback_hydrate_effect_deps)
  //
  // QUAN TRỌNG: chỉ hydrate khi query đã SETTLE (`!editLoading`). Khi mở lại
  // Edit, editData đã invalidate (lần Save trước) nên RTK refetch — trong lúc
  // refetch nó phục vụ CACHE CŨ trước. Nếu hydrate ngay với cache cũ → set ref
  // → data fresh về bị skip → Form kẹt giá trị cũ. Đợi `!editLoading` mới
  // hydrate ⇒ luôn lấy giá trị fresh từ server.
  // Contract mà parent truyền khi mở dialog (Edit từ list). Dùng để CHỈ show
  // spinner khởi tạo cho lần load đầu (open-immediately + in-dialog spinner,
  // chuẩn LocalPFormDialog). KHÔNG show khi user tự đổi contract trong
  // Autocomplete (giữ form + Autocomplete usable).
  const initialContractRef = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      initialContractRef.current = null
      return
    }
    initialContractRef.current = contractNo ?? null
  }, [open, contractNo])

  const hydratedContractRef = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      hydratedContractRef.current = null
      return
    }
    if (editLoading) return
    if (!editData || rowCount === 0) return
    if (hydratedContractRef.current === activeContractNo) return
    hydratedContractRef.current = activeContractNo
    setForm((prev) => ({
      ...prev,
      releaseDate: safeDate(editData.releaseDate) || prev.releaseDate || today(),
      isUpload: Boolean(editData.isUpload),
      isCusSign: Boolean(editData.isCusSign),
    }))
  }, [open, editData, rowCount, activeContractNo, editLoading])

  useEffect(() => {
    if (form.contractNo && !form.releaseDate) {
      setForm((prev) => ({ ...prev, releaseDate: today() }))
    }
  }, [form.contractNo, form.releaseDate])

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const set = <K extends keyof SalePTBFFixationFormModel>(key: K, value: SalePTBFFixationFormModel[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    clearFieldError(key as string)
  }

  const setNum = (key: keyof SalePTBFFixationFormModel, raw: string) => {
    setForm((prev) => ({ ...prev, [key]: raw.replace(/,/g, '') }))
    clearFieldError(key as string)
  }

  // Validate phần input row (Date fix / Lots fixed / Level fixed). Dùng cho cả
  // "Save Row" (subform input) và "Save" (dialog title) khi muốn save row.
  const validateRowInput = (): { message: string | null; errors: Record<string, string> } => {
    const errors: Record<string, string> = {}
    const messages: string[] = []
    const addError = (k: string, m: string) => {
      if (!errors[k]) {
        errors[k] = m
        messages.push(m)
      }
    }
    if (!form.contractNo.trim()) addError('contractNo', 'Please choose Contract No!')
    if (!form.dateFix) addError('dateFix', 'Please choose Date Fixed!')
    if (form.lotsFixed === '') addError('lotsFixed', 'Please type Lots Fixed!')
    if (form.lotsFixed !== '' && Number(form.lotsFixed) <= 0) {
      addError('lotsFixed', "'Lots Fixed' must be greater than 0!")
    }
    if (form.levelFixed === '') addError('levelFixed', 'Please type Level Fixed!')
    if (lotToFix != null && form.lotsFixed !== '' && Number(form.lotsFixed) > Number(lotToFix)) {
      addError('lotsFixed', "'Lots Fixed' must not be greater than 'Lot to fix'!")
    }
    return { message: messages[0] ?? null, errors }
  }

  // Reset subform input về trạng thái "New row".
  // CHỦ Ý: dùng `...prev` để GIỮ NGUYÊN contractNo + releaseDate + isUpload +
  // isCusSign (chỉ reset 3 field input của row: dateFix/lotsFixed/levelFixed).
  // Contract No chỉ thay đổi khi user tự chọn lại từ Autocomplete.
  const clearRowInput = () => {
    setEditingRowId(null)
    setForm((prev) => ({ ...prev, dateFix: today(), lotsFixed: '', levelFixed: '' }))
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next.dateFix
      delete next.lotsFixed
      delete next.levelFixed
      return next
    })
  }

  // Save Row (subform input) — core logic, trả về true khi lưu thành công:
  //   - editingRowId set → PATCH updateRow.
  //   - editingRowId null + chưa có fixation nào → POST createFixation (row đầu).
  //   - editingRowId null + đã có fixation → POST createFixation tiếp row tiếp theo.
  // Sau khi save: reset input + giữ form mở để user nhập tiếp.
  const saveRow = async (): Promise<boolean> => {
    const { message, errors } = validateRowInput()
    setFieldErrors(errors)
    if (message || Object.keys(errors).length > 0) {
      setValidationError(message ?? 'Please fix the highlighted fields.')
      return false
    }
    setValidationError(null)
    try {
      if (editingRowId != null) {
        // PATCH existing row
        await updateRow({
          ptbfFixationId: editingRowId,
          body: {
            dateFix: form.dateFix,
            lotsFixed: toNum(form.lotsFixed),
            levelFixed: toNum(form.levelFixed),
          },
        }).unwrap()
        snack.success('Row updated')
      } else {
        // POST new row (cùng endpoint dùng cho row đầu lẫn các row tiếp theo)
        await createFixation({
          contractNo: form.contractNo,
          dateFix: form.dateFix,
          releaseDate: form.releaseDate || today(),
          lotsFixed: toNum(form.lotsFixed),
          levelFixed: toNum(form.levelFixed),
          isUpload: form.isUpload ? 1 : 0,
          isCusSign: form.isCusSign ? 1 : 0,
        }).unwrap()
        snack.success('Row created')
      }
      // Reset input cho row tiếp theo, giữ form mở.
      clearRowInput()
      return true
    } catch (error) {
      setValidationError(extractApiErrorMessage(error))
      return false
    }
  }

  // "Save & Reload" button — chỉ lưu row, giữ form mở để nhập tiếp.
  const handleSaveRow = async () => {
    await saveRow()
  }

  // Save (dialog title). Theo yêu cầu chị Đẹp:
  //   - Chưa có fixation → Save = tạo row đầu (= Save Row).
  //   - Đã có fixation (Edit): CHỈ call event "Save & Reload" (saveRow) khi
  //     Lots fixed HOẶC Level fixed có value. Nếu cả 2 trống → bỏ qua row, chỉ
  //     lưu header. Khi có call saveRow mà fail (validation / API) → dừng,
  //     KHÔNG lưu header, KHÔNG đóng form.
  const handleSave = async () => {
    if (!form.contractNo.trim()) {
      setValidationError('Please choose Contract No!')
      return
    }
    // Chưa có fixation cho contract này → Save = tạo row đầu.
    if (!hasExistingFixation) {
      await saveRow()
      return
    }
    // Edit: chỉ lưu row khi Lots fixed / Level fixed có value.
    const hasRowValue = form.lotsFixed !== '' || form.levelFixed !== ''
    if (hasRowValue) {
      const ok = await saveRow()
      if (!ok) return
    }
    // Save header.
    setFieldErrors({})
    setValidationError(null)
    try {
      const body: SalePTBFFixationPayload = {
        contractNo: form.contractNo,
        releaseDate: form.releaseDate || today(),
        isUpload: form.isUpload ? 1 : 0,
        isCusSign: form.isCusSign ? 1 : 0,
      }
      await updateEditOptions({ contractNo: form.contractNo, body }).unwrap()
      snack.success('Successfully Updated')
      handleCancel()
    } catch (error) {
      setValidationError(extractApiErrorMessage(error))
    }
  }

  const handleCancel = () => {
    setForm(emptyForm())
    setEditingRowId(null)
    setValidationError(null)
    setFieldErrors({})
    onClose()
  }

  // Click Edit trên row subform → load row data vào input area để sửa.
  const handleRowEdit = (row: SalePTBFFixationRow) => {
    if (row.ptbfFixationId == null) return
    setEditingRowId(row.ptbfFixationId)
    setForm((prev) => ({
      ...prev,
      dateFix: safeDate(row.dateFix) || today(),
      lotsFixed: row.lotsFixed != null ? String(row.lotsFixed) : '',
      levelFixed: row.levelFixed != null ? String(row.levelFixed) : '',
    }))
    setFieldErrors((prev) => {
      const next = { ...prev }
      delete next.dateFix
      delete next.lotsFixed
      delete next.levelFixed
      return next
    })
  }

  const handleRowDelete = (row: SalePTBFFixationRow) => {
    setRowDeleteTarget(row)
  }

  const handleConfirmRowDelete = async () => {
    if (!rowDeleteTarget?.ptbfFixationId) return
    const deletedId = rowDeleteTarget.ptbfFixationId
    setRowDeleteTarget(null)
    try {
      await cancelFixation(deletedId).unwrap()
      // RTK Query invalidate `'SalePTBFFixation'` → tự refetch rows + editData
      // + lotToFix (vừa thêm providesTags). Không cần explicit refetchRows.
      // Nếu vừa xoá row đang edit trong input → reset input về New mode.
      if (editingRowId === deletedId) clearRowInput()
      snack.success('Successfully Deleted')
    } catch (err) {
      snack.error(getErrorMessage(err, 'Could not delete PTBF Fixation.'))
    }
  }

  // In-dialog loading (open-immediately + spinner, chuẩn LocalPFormDialog):
  // mở Edit cho contract có sẵn → show spinner tới khi editData/rows settle.
  // Cache hit → không fetching + data có sẵn → hydrate đồng bộ → KHÔNG spinner.
  // Chỉ áp cho contract khởi tạo; sau khi hydrate xong (hoặc user đổi contract)
  // → không show lại (tránh chớp spinner khi refetch sau Save).
  const showInitialLoading =
    open &&
    !!activeContractNo &&
    activeContractNo === initialContractRef.current &&
    hydratedContractRef.current !== activeContractNo &&
    (editLoading || rowsFetching || !editData || !rowsData)

  const title = hasExistingFixation ? 'Edit - Sale PTBF Fixation' : 'Sale PTBF Fixation'

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            component="span"
            noWrap
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: 1, fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
          >
            {hasExistingFixation && (
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
                Edit -{' '}
              </Box>
            )}
            Sale PTBF Fixation
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={handleSave}
              disabled={creating || updating || updatingRow || editLoading || !canEdit}
              sx={{ py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', lineHeight: 1.2, textTransform: 'none' }}
            >
              {creating || updating || updatingRow || editLoading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCancel}
              sx={{ py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', lineHeight: 1.2, textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent dividers sx={dialogContentSx}>
          {showInitialLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
          ) : (
          <>
          {validationError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {validationError}
            </Alert>
          )}

          {/* ── Unified form: header always shown, Contract No always Autocomplete ── */}
          <Box>
            {/* Header fields — Contract No autocomplete + Issued Date + checkbox flags */}
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6} md={4}>
                {(() => {
                  const selectedContract: LookupItem | null = form.contractNo
                    ? contractItems.find((o) => o.value === form.contractNo) ?? { value: form.contractNo, label: form.contractNo }
                    : null
                  // Inject fallback vào options → tắt MUI "none of options match" (server-search).
                  // CHỈ inject khi KHÔNG đang gõ search (text ≠ label đã chọn) — filterOptions
                  // {(x)=>x} sẽ hiển thị cả dòng inject không khớp text (bug "1 dòng đúng + dòng cũ").
                  const searchingContract = !!contractSearchText && contractSearchText !== (selectedContract?.label ?? '')
                  const contractOpts = !searchingContract && selectedContract && !contractItems.some((o) => o.value === selectedContract.value)
                    ? [selectedContract, ...contractItems]
                    : contractItems
                  return (
                    <Autocomplete
                      size="small"
                      options={contractOpts}
                      loading={contractsLoading}
                      value={selectedContract}
                      inputValue={contractSearchText}
                      getOptionLabel={(o: LookupItem | string) => (typeof o === 'string' ? o : o.label)}
                      isOptionEqualToValue={(o, v) => o.value === v.value}
                      onInputChange={(_, value, reason) => {
                        // 'input' = user gõ; 'reset' = MUI internal sau select/clear.
                        // Chỉ debounce theo input của user, không trigger fetch khi MUI reset.
                        if (reason === 'input') setContractSearchText(value)
                        else if (reason === 'reset') setContractSearchText(value)
                      }}
                      onChange={(_, item) => {
                        set('contractNo', item?.value ?? '')
                        // Update inputValue cho match với option đã chọn → tránh
                        // text gõ dở khác với value đã commit.
                        setContractSearchText(item ? item.label : '')
                      }}
                      filterOptions={(x) => x}
                      // KHÔNG dùng clearOnBlur — giữ text khi user blur, tránh mất gõ dở.
                      handleHomeEndKeys
                      autoHighlight
                      selectOnFocus
                      openOnFocus
                      loadingText="Searching contracts..."
                      noOptionsText={contractSearchText ? 'No matching contract' : 'Type to search'}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Contract no"
                          error={!!fieldErrors.contractNo}
                          helperText={fieldErrors.contractNo}
                        />
                      )}
                    />
                  )
                })()}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Issued Date"
                  format="DD-MMM-YYYY"
                  value={form.releaseDate ? dayjs(form.releaseDate) : null}
                  onChange={(d) => set('releaseDate', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Lot to Fix" size="small" fullWidth value={lotToFix ?? ''} InputProps={{ readOnly: true }} sx={roSx} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={<Checkbox checked={form.isUpload} onChange={(e) => set('isUpload', e.target.checked)} />}
                  label="Is Uploaded?"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={<Checkbox checked={form.isCusSign} onChange={(e) => set('isCusSign', e.target.checked)} />}
                  label="Customer Signed?"
                />
              </Grid>
            </Grid>

            {/* ── Section "Fixation Row" — luôn hiển thị, dùng làm New / Edit row input.
                  Concept tham khảo /purchase/contract/plans subForm1 + nút "New".
                  - editingRowId null → input đang ở trạng thái New row
                  - editingRowId set  → input đang edit row đó (nút "New" hiện) ── */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 1, mb: 0.75, rowGap: 0.5 }}
              spacing={1}
            >
              <Typography
                variant="subtitle2"
                noWrap
                title={`Fixation Row${editingRowId != null ? ` — Editing ID #${editingRowId}` : ''}`}
                sx={{ ...TEXT_TRUNCATE, flex: 1, minWidth: 0, fontWeight: 700, fontSize: '0.8125rem' }}
              >
                Fixation Row
                {editingRowId != null ? ` — Editing ID #${editingRowId}` : ''}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                {editingRowId != null && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={clearRowInput}
                    disabled={creating || updatingRow}
                    sx={{ py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', textTransform: 'none' }}
                  >
                    New
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => void handleSaveRow()}
                  disabled={!form.contractNo || creating || updatingRow || !canEdit}
                  sx={{ py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem', textTransform: 'none' }}
                >
                  {creating || updatingRow ? <CircularProgress size={14} color="inherit" /> : 'Save & Reload'}
                </Button>
              </Stack>
            </Stack>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Date fix"
                  format="DD-MMM-YYYY"
                  value={form.dateFix ? dayjs(form.dateFix) : null}
                  onChange={(d) => set('dateFix', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
                  disabled={!form.contractNo}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      InputLabelProps: { shrink: true },
                      error: !!fieldErrors.dateFix,
                      helperText: fieldErrors.dateFix,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Lots fixed"
                  size="small"
                  fullWidth
                  value={fmtNumStr(form.lotsFixed)}
                  onChange={(e) => setNum('lotsFixed', e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={numSx}
                  error={!!fieldErrors.lotsFixed}
                  helperText={fieldErrors.lotsFixed}
                  disabled={!form.contractNo}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Level fixed"
                  size="small"
                  fullWidth
                  value={fmtNumStr(form.levelFixed)}
                  onChange={(e) => setNum('levelFixed', e.target.value)}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={numSx}
                  error={!!fieldErrors.levelFixed}
                  helperText={fieldErrors.levelFixed}
                  disabled={!form.contractNo}
                />
              </Grid>
            </Grid>

            {/* ── Subform table — luôn hiển thị (kể cả empty) ── */}
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ overflow: 'auto' }}>
              <Table size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'background.default' }}>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 }}>Contract No</TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 }}>Date fix</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 }}>Lots fixed</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 }}>Level fixed</TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5 }}>Functions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subformRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 1, fontSize: '0.8125rem' }}>
                        No records — type values above and click "Save &amp; Reload" to add the first row.
                      </TableCell>
                    </TableRow>
                  ) : (
                    subformRows.map((row) => {
                      const isEditingThis = row.ptbfFixationId === editingRowId
                      return (
                        <TableRow
                          key={row.ptbfFixationId}
                          hover
                          selected={isEditingThis}
                          sx={{ '& td': { py: 0.375, fontSize: '0.8125rem', whiteSpace: 'nowrap' } }}
                        >
                          <TableCell>{row.contractNo ?? ''}</TableCell>
                          <TableCell>{formatAccessDate(row.dateFix)}</TableCell>
                          <TableCell align="right">{formatNum(row.lotsFixed ?? null)}</TableCell>
                          <TableCell align="right">{formatNum(row.levelFixed ?? null)}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Button
                                size="small"
                                variant={isEditingThis ? 'contained' : 'outlined'}
                                disabled={!canEdit}
                                sx={{ minWidth: 0, px: 0.75, py: 0.15, fontSize: '0.72rem', textTransform: 'none' }}
                                onClick={() => handleRowEdit(row)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={!canEdit}
                                sx={{ minWidth: 0, px: 0.75, py: 0.15, fontSize: '0.72rem', textTransform: 'none' }}
                                onClick={() => handleRowDelete(row)}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
            {/* Footer: row count */}
            <Box sx={{ mt: 0.5, px: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Count: {rowCount}
              </Typography>
            </Box>
          </Box>
          </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={rowDeleteTarget != null}
        title="Delete PTBF Fixation"
        message={`Do you want to delete this PTBF Fixation = '${rowDeleteTarget?.contractNo ?? ''}'?`}
        confirmText="Delete"
        destructive
        loading={isDeleting}
        onConfirm={() => void handleConfirmRowDelete()}
        onCancel={() => setRowDeleteTarget(null)}
      />
    </>
  )
}
